import { Heading } from '@tiptap/extension-heading'
export function parseHTMLContent(htmlContent: string): any[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')
  const nodes = Array<any>()

  doc.body.childNodes.forEach(element => {
    const node = parseNode(element)
    if (node) {
      nodes.push(node)
    }
  })

  return nodes
}

function parseNode(element: any): any {
  if (element.nodeType === Node.TEXT_NODE) {
    // Only return text nodes that have content
    return element.textContent
      ? {
          type: 'text',
          text: element.textContent,
        }
      : null
  }

  if (element.nodeType === Node.ELEMENT_NODE) {
    const tagName = element.nodeName.toLowerCase()

    if (tagName === 'p') {
      const content = parseChildren(element)
      // Filter out null values and flatten the array
      return {
        type: 'paragraph',
        content: content.filter(Boolean),
      }
    }

    // Handle <br> tags by converting them to hard breaks
    if (tagName === 'br') {
      return {
        type: 'hardBreak',
      }
    }

    const attributeType = element.getAttribute('data-type')
    if (
      tagName === 'a' &&
      ['mention', 'channel-mention', 'events-mention'].includes(attributeType)
    ) {
      const slug =
        element.getAttribute('data-slug') ||
        element.getAttribute('href')?.split('/').slice(-1)[0] ||
        ''
      const attrs = {
        id: element.getAttribute('data-id') || '',
        label: element.getAttribute('data-label') || '',
        slug,
        type: attributeType,
        icon: element.getAttribute('data-icon') || '',
        timestamp: element.getAttribute('data-timestamp') || '',
      }
      if (attributeType === 'events-mention') {
        attrs.timestamp = element.getAttribute('data-timestamp') || ''
      }

      // Map mention types to their corresponding TipTap extensions
      let nodeType = attributeType
      if (
        attributeType === 'channel-mention' ||
        attributeType === 'events-mention'
      ) {
        nodeType = 'other-mention'
      }

      return {
        type: nodeType,
        attrs,
      }
    }

    if (tagName === 'a') {
      // Regular link
      const attrs = {
        href: element.getAttribute('href') || '',
      }

      return {
        type: 'text',
        text: element.textContent || '',
        marks: [
          {
            type: 'link',
            attrs,
          },
        ],
      }
    }
    if (tagName === 'img') {
      const attrs = Object.fromEntries(
        Array.from(
          element.attributes as NamedNodeMap,
          (attr: Attr): [string, string] => [attr.name, attr.value]
        )
      )
      return {
        type: 'image',
        attrs,
      }
    }

    if (tagName === 'span' && element.classList.contains('emoji')) {
      return {
        type: 'text',
        text: element.textContent || '',
        marks: [
          {
            type: 'textStyle',
            attrs: {
              class: 'emoji',
            },
          },
        ],
      }
    }

    // Handle @everyone broadcast mention spans - preserve exact HTML structure
    if (
      tagName === 'span' &&
      element.getAttribute('data-type') === 'mention' &&
      element.getAttribute('data-label') === 'everyone' &&
      element.getAttribute('data-mention-type') === 'broadcast'
    ) {
      return {
        type: 'text',
        text: element.textContent || '',
        marks: [
          {
            type: 'textStyle',
            attrs: {
              'data-type': element.getAttribute('data-type'),
              'data-label': element.getAttribute('data-label'),
              'data-mention-type': element.getAttribute('data-mention-type'),
            },
          },
        ],
      }
    }

    // Handle regular span tags - extract text content
    if (tagName === 'span') {
      return {
        type: 'text',
        text: element.textContent || '',
      }
    }

    // Handle strong/bold tags
    if (tagName === 'strong' || tagName === 'b') {
      return {
        type: 'text',
        text: element.textContent || '',
        marks: [
          {
            type: 'bold',
          },
        ],
      }
    }

    // Handle emphasis/italic tags
    if (tagName === 'em' || tagName === 'i') {
      return {
        type: 'text',
        text: element.textContent || '',
        marks: [
          {
            type: 'italic',
          },
        ],
      }
    }

    // Handle heading tags
    if (tagName.match(/^h[1-6]$/)) {
      const level = parseInt(tagName.charAt(1))
      const content = parseChildren(element)
      return {
        type: 'heading',
        attrs: {
          level: level,
        },
        content: content.filter(Boolean),
      }
    }

    if (tagName === 'ul') {
      const content = parseChildren(element)
      return {
        type: 'bulletList',
        content: content.filter(Boolean),
      }
    }

    if (tagName === 'ol') {
      const content = parseChildren(element)
      return {
        type: 'orderedList',
        content: content.filter(Boolean),
      }
    }

    if (tagName === 'li') {
      const content = parseChildren(element)
      const filteredContent = content.filter(Boolean)

      // Filter out whitespace-only text nodes (HTML formatting artifacts like '\n')
      const cleanContent = filteredContent.filter(node => {
        if (node.type === 'text' && (!node.text || node.text.trim() === '')) {
          return false // Remove whitespace-only text nodes
        }
        return true
      })

      // Separate block and inline content
      const blockContent = cleanContent.filter(node => {
        return (
          node.type === 'paragraph' ||
          node.type === 'bulletList' ||
          node.type === 'orderedList' ||
          node.type === 'heading' ||
          node.type === 'blockquote'
        )
      })

      const inlineContent = cleanContent.filter(node => {
        return (
          node.type === 'text' ||
          node.type === 'hardBreak' ||
          node.marks ||
          (node.type !== 'paragraph' &&
            node.type !== 'bulletList' &&
            node.type !== 'orderedList' &&
            node.type !== 'heading' &&
            node.type !== 'blockquote')
        )
      })

      // If we have block content, use it (with any remaining inline content wrapped)
      if (blockContent.length > 0) {
        let finalContent = [...blockContent]

        // If there's also inline content, wrap it in a paragraph and add it
        if (inlineContent.length > 0) {
          finalContent.push({
            type: 'paragraph',
            content: inlineContent,
          })
        }

        return {
          type: 'listItem',
          content: finalContent,
        }
      }

      // Only inline content - wrap in paragraph
      if (inlineContent.length > 0) {
        return {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: inlineContent,
            },
          ],
        }
      }

      // Empty list item
      return {
        type: 'listItem',
        content: [
          {
            type: 'paragraph',
            content: [],
          },
        ],
      }
    }

    // Fallback for all other HTML tags - extract text content
    if (element.textContent && element.textContent.trim()) {
      return {
        type: 'text',
        text: element.textContent,
      }
    }

    return null
  }

  return null
}

function parseChildren(element: Node): any[] {
  const children = new Array<any>()

  element.childNodes.forEach(child => {
    const node = parseNode(child)
    if (node) {
      children.push(node)
    }
  })

  return children
}

export function sanitizeJavaScriptCode(jsCode: string | null | undefined = '') {
  // Remove HTML tags and leading/trailing white spaces
  return jsCode?.replace(/<\/?[^>]+(>|$)/g, '').trim()
}

// This extension will disable markdown syntax like #(which applies css), because it conflicts with mentions
// Use Case: while tagging with #, editor starts treating this has and complete mention as h1 tag,this function will disable this behavior
export const HeadingConfiguration = Heading.configure({
  HTMLAttributes: {},
  levels: [1, 2, 3, 4, 5, 6],
}).extend({
  addInputRules() {
    return [] // Disable markdown syntax like #, because it conflicts with mentions
  },
})
